import React, { useState, useEffect, RefObject, ForwardRefRenderFunction, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import './nav.scss';

interface NavRef {
}

export interface NavPropsRefs {
  hero: RefObject<HTMLElement>,
  clients: RefObject<HTMLElement>,
  about: RefObject<HTMLElement>,
  tech: RefObject<HTMLElement>,
  services: RefObject<HTMLElement>,
  process: RefObject<HTMLElement>,
  projects: RefObject<HTMLElement>,
  faq: RefObject<HTMLElement>,
  contact: RefObject<HTMLElement>
}

interface NavProps {
  headerElRef: RefObject<HTMLElement>,
  refs: NavPropsRefs
}

const keyToHash = (key: string): string => {
  const map: Record<string, string> = {
    'hero': 'home',
    'clients': 'home',
    'about': 'about',
    'tech': 'about',
    'services': 'services',
    'process': 'process',
    'projects': 'projects',
    'faq': 'faq',
    'contact': 'contact'
  };

  return map[key] || key;
};

const Nav: ForwardRefRenderFunction<NavRef, NavProps> = (props: NavProps, ref: React.ForwardedRef<NavRef>) => {

  const { refs, headerElRef } = props;

  const navigate = useNavigate();

  const [ hashInit, setHashInit ] = useState<boolean>(false);
  const [ mobileMenu, setMobileMenu ] = useState<boolean>(false);
  const [ currentNav, setCurrentNav ] = useState<string>('hero');

  const updateActiveLink = useCallback(() => {
    const position = window.scrollY + 200;

    // We are at the bottom of the page
    if (position >= (document.body.scrollHeight - window.innerHeight)) {
      setCurrentNav('contact');
      return;
    }

    for (const key in props.refs) {
      const ref = props.refs[key as keyof typeof props.refs];

      if (ref.current) {
        if ((position >= ref.current.offsetTop) && (position <= (ref.current.offsetTop + ref.current.offsetHeight))) {
          if (key !== currentNav) {
            setCurrentNav(key);
          }
          return;
        }
      }
    }
  }, [ props, currentNav ]);

  const onNavHash = useCallback((hash: string) => {
    const findRef = (hash: string): React.RefObject<HTMLElement> | null => {
      for (const key in refs) {
        if (key === hash) {
          const ref = refs[key as keyof typeof refs];
          if ((ref) && (ref.current)) {
            return ref;
          }
        }
      }

      return null;
    };

    if ((hash === '') || (hash === 'home')) {

      setMobileMenu(false);

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      return;
    }

    const ref = findRef(hash);

    if ((!ref) || (!ref.current)) {
      return;
    }
    if (!headerElRef.current) {
      return;
    }

    setMobileMenu(false);

    const offset = headerElRef.current.offsetHeight;
    const elementPos = ref.current.offsetTop;

    window.scrollTo({
      top: elementPos - offset,
      behavior: 'smooth'
    });
  }, [ refs, headerElRef ]);

  useEffect(() => {
    const onLoad = () => {
      if ((!hashInit) && (window.location.hash.length)) {
        onNavHash(window.location.hash.replace('#', ''));
      }

      setHashInit(true);
    };

    window.addEventListener('load', onLoad);

    return () => {
      window.removeEventListener('load', onLoad);
    }

  }, [ onNavHash, hashInit ]);

  useEffect(() => {
    const onScroll = () => {
      updateActiveLink();
    };
    const onHashChange = () => {
      onNavHash(window.location.hash.replace('#', ''));
    };

    document.addEventListener('scroll', onScroll);
    window.addEventListener('hashchange', onHashChange);

    return () => {
      document.removeEventListener('scroll', onScroll);
      window.removeEventListener('hashchange', onHashChange);
    }
  }, [ updateActiveLink, onNavHash ]);

  useEffect(() => {
    if (!hashInit) return;

    const hash = keyToHash(currentNav);
    const expectedHash = `#${hash}`;

    if (hash === 'home' && !window.location.hash && window.scrollY === 0) {
      return;
    }

    if (window.location.hash !== expectedHash) {
      navigate(expectedHash, { replace: true });
    }
  }, [currentNav, hashInit, navigate]);

  const buildNavClasses = (nav: string[]): string => {
    return [ 'nav-link', ...(nav.includes(currentNav) ? ['active'] : []) ].join(' ');
  };

  const onMobileToggleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenu(!mobileMenu);
  };

  return (
    <nav className={['component-navbar', ...(mobileMenu ? ['component-navbar-mobile'] : [''])].join(' ')}>
      <ul>
        <li><a className={buildNavClasses(['hero', 'clients'])} href="#home">Home</a></li>
        <li><a className={buildNavClasses(['about', 'tech'])} href="#about">About</a></li>
        <li><a className={buildNavClasses(['services'])} href="#services">Services</a></li>
        <li><a className={buildNavClasses(['process'])} href="#process">How We Work</a></li>
        <li><a className={buildNavClasses(['projects'])} href="#projects">Our Work</a></li>
        <li><a className={buildNavClasses(['faq'])} href="#faq">Often Asked</a></li>
        <li><a className="cta" href="#contact">Discuss Project</a></li>
      </ul>
      <i className={['bi', 'mobile-toggle', ...(mobileMenu ? ['bi-x'] : ['bi-list'])].join(' ')} onClick={onMobileToggleClick}></i>
    </nav>
  );
};

export default React.forwardRef(Nav);